/**
 * Mobile Impact Navigation Menu
 *
 * This file handles the mobile impact navigation functionality including:
 * - Sticky trigger behavior
 * - Menu slide animations
 * - Active section detection and updates
 * - Smooth scroll functionality
 * - Background color crossfade integration
 */

(function(document, $) {
	'use strict';

	// State management
	let isMenuOpen = false;
	let lastActiveSectionIndex = -1;

	// DOM elements will be cached in initMobileImpactNav()
	let $trigger, $mobileMenu, $contentImpact, $sections, $contentOuterWrap;

	// Section data will be populated from actual menu items
	let sectionData = [];

	// Make the breakpoint a variable, for easy change; must be changed both here and in menu.js
	const breakpoint = 1200;

	/**
	 * Initialize mobile impact navigation
	 *
	 * Sets up all mobile navigation functionality including:
	 * - DOM element caching
	 * - Event handlers
	 * - Sticky behavior
	 * - Smooth scroll
	 * - Background crossfade integration
	 */
	function initMobileImpactNav() {
		// Cache DOM elements now that they exist
		$trigger = $('#nav-impact-mobile-trigger');
		$mobileMenu = $('#nav-impact-mobile');
		$contentImpact = $('.content-impact');
		$sections = $('.content-impact > .chunk');
		$contentOuterWrap = $('.content-impact-outer-wrap');

		if (!$trigger.length || !$mobileMenu.length) {
			return;
		}

		// Set up event handlers
		setupEventHandlers();

		// Set up sticky behavior
		initStickyBehavior();

		// Set up smooth scroll for menu items
		initMobileSmoothScroll();

		// Populate section data from actual menu items
		populateSectionData();

		// Hook into existing background crossfade system
		hookIntoBackgroundCrossfade();

		// Initial state
		updateTriggerContent();
		updateTriggerBackground();
	}

	/**
	 * Populate section data from actual menu items
	 *
	 * Reads the WordPress menu structure and extracts:
	 * - Section titles from menu link text
	 * - Categories (Nature/People) from CSS classes
	 * - Icons from CSS classes
	 *
	 * This ensures the mobile trigger displays the same content as the desktop menu
	 */
	function populateSectionData() {
		const $navItems = $mobileMenu.find('.sub-menu > li');
		sectionData = [];

		$navItems.each(function(index) {
			const $item = $(this);
			const $link = $item.find('a');
			const title = $link.text().trim();

			// Determine category and icon class based on classes
			// This should match the desktop navigation logic
			let category = getParentItemLabel($item); // Default
			let iconClass = 'impact-icon-globe'; // Default

			if ($item.hasClass('impact-icon-globe')) {
				iconClass = 'impact-icon-globe';
			} else if ($item.hasClass('impact-icon-tree-shield')) {
				iconClass = 'impact-icon-tree-shield';
			} else if ($item.hasClass('impact-icon-grain')) {
				iconClass = 'impact-icon-grain';
			} else if ($item.hasClass('impact-icon-people')) {
				iconClass = 'impact-icon-people';
			} else if ($item.hasClass('impact-icon-heartman')) {
				iconClass = 'impact-icon-heartman';
			}

			sectionData.push({
				title: title,
				category: category,
				iconClass: iconClass
			});
		});
	}

	/**
	 * Get the label from the parent item, which is wrapped in a span thanks to code in the php part.
	 *
	 * @param $item
	 * @return {*}
	 */
	function getParentItemLabel( $item ) {
		const $parentItem = $item.closest('ul').parent('li');
		const $parentSpan = $parentItem.find('> span');
		return $parentSpan.text().trim();
	}


	/**
	 * Hook into the existing background crossfade system
	 *
	 * Listens for scroll events and updates mobile navigation elements:
	 * - Trigger content (title, icon, category)
	 * - Trigger background color
	 * - Menu background color
	 *
	 * This ensures the mobile navigation stays synchronized with the main content
	 */
	function hookIntoBackgroundCrossfade() {
		// Listen for scroll events and update mobile navigation
		$(window).on('scroll.mobileNav', function() {
			updateTriggerContent();
			updateTriggerBackground();
		});
	}

	/**
	 * Update trigger and mobile menu background color to match the outer wrapper
	 *
	 * Copies the background color from .content-impact-outer-wrap to both:
	 * - The mobile trigger element
	 * - The mobile menu element
	 *
	 * This creates a seamless visual experience where the mobile navigation
	 * matches the main content's background color crossfade behavior
	 */
	function updateTriggerBackground() {
		const outerWrapColor = $contentOuterWrap.css('background-color');
		if (outerWrapColor && outerWrapColor !== 'rgba(0, 0, 0, 0)' && outerWrapColor !== 'transparent') {
			$trigger.css('background-color', outerWrapColor);
			$mobileMenu.css('background-color', outerWrapColor);
		}
	}

	/**
	 * Set up all event handlers for mobile navigation
	 *
	 * Configures event listeners for:
	 * - Trigger click (toggle menu)
	 * - Document click (close menu when clicking outside)
	 * - Window resize (handle breakpoint transitions)
	 * - Escape key (close menu)
	 */
	function setupEventHandlers() {
		// Trigger click handler
		$trigger.on('click', handleTriggerClick);

		// Resize handler
		$(window).on('resize', handleResize);

		// Click outside to close menu
		$(document).on('click', handleClickOutside);

		// Escape key to close menu
		$(document).on('keydown', handleEscapeKey);
	}

	/**
	 * Handle trigger click - toggle menu
	 *
	 * Prevents default behavior and stops event propagation to avoid
	 * conflicts with other click handlers. Then toggles the menu state.
	 */
	function handleTriggerClick(e) {
		e.preventDefault();
		e.stopPropagation();
		toggleMenu();
	}

	/**
	 * Toggle mobile menu visibility
	 *
	 * Manages the menu open/close state and applies appropriate CSS classes
	 * and animations. Uses jQuery slideDown/slideUp for smooth transitions.
	 */
	function toggleMenu() {
		isMenuOpen = !isMenuOpen;

		if (isMenuOpen) {
			$trigger.addClass('menu-open');
			$mobileMenu.slideDown('fast');
		} else {
			$trigger.removeClass('menu-open');
			$mobileMenu.slideUp('fast');
		}
	}

	/**
	 * Close menu
	 */
	function closeMenu() {
		if (isMenuOpen) {
			isMenuOpen = false;
			$trigger.removeClass('menu-open');
			$mobileMenu.slideUp('fast');
		}
	}

	/**
	 * Handle click outside menu
	 */
	function handleClickOutside(e) {
		if (isMenuOpen && !$trigger.is(e.target) && !$mobileMenu.is(e.target) && $mobileMenu.has(e.target).length === 0) {
			closeMenu();
		}
	}

	/**
	 * Handle escape key
	 */
	function handleEscapeKey(e) {
		if (e.keyCode === 27 && isMenuOpen) {
			closeMenu();
		}
	}


	/**
	 * Reset sticky state and remove any applied padding
	 *
	 * Cleans up sticky positioning and padding when transitioning between
	 * desktop and mobile breakpoints. This prevents layout issues when
	 * resizing the viewport.
	 */
	function resetStickyState() {
		if (!$trigger.length || !$contentOuterWrap.length) return;

		// Remove padding from content
		$contentOuterWrap.css('padding-top', '');

		// Reset trigger positioning
		$trigger.css({
			'position': '',
			'top': '',
			'z-index': ''
		});
	}

	// Track previous window width to detect breakpoint transitions
	let previousWindowWidth = $(window).width();

	/**
	 * Handle resize events
	 *
	 * Manages breakpoint transitions between desktop and mobile:
	 * - Closes menu on resize to prevent layout issues
	 * - Detects when crossing the breakpoint
	 * - Resets sticky state when transitioning between breakpoints
	 * - Reinitializes mobile sticky behavior when switching to mobile
	 *
	 * This ensures smooth transitions and prevents layout conflicts
	 * between desktop and mobile navigation systems.
	 */
	function handleResize() {
		// Close menu on resize to prevent layout issues
		closeMenu();

		const currentWidth = $(window).width();
		const isCurrentlyMobile = currentWidth < breakpoint;
		const wasMobile = previousWindowWidth < breakpoint;

		// Only reset sticky state when transitioning between desktop and mobile
		if (isCurrentlyMobile !== wasMobile) {
			resetStickyState();

			// Reinitialize sticky behavior for mobile
			if (isCurrentlyMobile) {
				initStickyBehavior();
			}
		}

		previousWindowWidth = currentWidth;
		updateTriggerContent();
	}

	/**
	 * Update trigger content based on active section
	 *
	 * Updates the mobile trigger display with:
	 * - Current section title
	 * - Appropriate icon class
	 * - Section category (Nature/People)
	 * - Background color for category badge
	 *
	 * This keeps the trigger synchronized with the user's scroll position
	 * and the active content section.
	 */
	function updateTriggerContent() {
		const activeIndex = getActiveSectionIndex();

		if (activeIndex >= 0 && activeIndex < sectionData.length) {
			const section = sectionData[activeIndex];

			// Update trigger content
			const $titleElement = $trigger.find('.section-title');
			$titleElement.text(section.title);

			// Update icon class - remove all impact-icon classes and add the correct one
			$titleElement.removeClass('impact-icon-globe impact-icon-tree-shield impact-icon-grain impact-icon-people impact-icon-heartman');
			$titleElement.addClass(section.iconClass);

			$trigger.find('.section-category').text(section.category);

			// Update section category background color based on icon
			const $categoryElement = $trigger.find('.section-category');
			$categoryElement.removeClass('bg-ocean-100 bg-forest-100 bg-lime-100 bg-sand-100');

			// Map icon class to background color class
			const colorMap = {
				'impact-icon-globe': 'bg-ocean-100',
				'impact-icon-tree-shield': 'bg-forest-100',
				'impact-icon-grain': 'bg-lime-100',
				'impact-icon-people': 'bg-sand-100',
				'impact-icon-heartman': 'bg-sand-100'
			};

			const bgClass = colorMap[section.iconClass] || 'bg-ocean-100';
			$categoryElement.addClass(bgClass);
		}
	}

	/**
	 * Get currently active section index
	 *
	 * Determines which section is currently in view based on scroll position.
	 * Uses a 33% viewport threshold to determine when a section becomes active.
	 *
	 * @returns {number} Index of the active section, or 0 if not scrolled to content yet
	 */
	function getActiveSectionIndex() {
		const scrollY = window.scrollY || window.pageYOffset;
		const windowHeight = $(window).height();
		const viewportTop = scrollY;
		const viewportBottom = scrollY + windowHeight;

		if (!$contentImpact.length) {
			return -1;
		}

		const contentTop = $contentImpact.offset().top;

		if (scrollY < contentTop) {
			return 0; // Return first section when not scrolled to content yet
		}

		let currentSectionIndex = -1;

		$sections.each(function(index) {
			const $section = $(this);
			const sectionTop = $section.offset().top;
			const sectionBottom = sectionTop + $section.outerHeight();

			// Check if section is visible in viewport
			if (sectionBottom > viewportTop && sectionTop < viewportBottom) {
				if (currentSectionIndex === -1) {
					currentSectionIndex = index;
				}
			}
		});

		return currentSectionIndex;
	}

	/**
	 * Update active states for both trigger and menu
	 */
	function updateActiveStates() {
		const currentSectionIndex = getActiveSectionIndex();

		// Only update if section has changed
		if (currentSectionIndex === lastActiveSectionIndex) return;

		// Update trigger content
		updateTriggerContent();

		// Update menu active states
		updateMenuActiveStates(currentSectionIndex);

		lastActiveSectionIndex = currentSectionIndex;
	}

	/**
	 * Update menu active states
	 */
	function updateMenuActiveStates(currentSectionIndex) {
		const $navItems = $mobileMenu.find('.sub-menu > li');
		if (!$navItems.length) return;

		// Remove active state from all items
		$navItems.find('a').removeClass('active');

		// Add active state to current section
		if (currentSectionIndex >= 0 && currentSectionIndex < $navItems.length) {
			$navItems.eq(currentSectionIndex).find('a').addClass('active');
		}
	}

	/**
	 * Initialize sticky behavior for mobile trigger
	 *
	 * Makes the mobile trigger sticky when scrolled past its initial position,
	 * but allows it to slide up when its bottom reaches the bottom of the
	 * content wrapper. Adds padding to prevent content jumping.
	 *
	 * This replicates the desktop navigation's sticky behavior for mobile.
	 */
	function initStickyBehavior() {
		if (!$trigger.length || !$contentOuterWrap.length) return;

		const initialTriggerOffset = $trigger.offset().top;
		let isSticky = false;

		function updateStickyState() {
			const scrollTop = $(window).scrollTop();
			const triggerHeight = $trigger.outerHeight();
			const contentBottom = $contentOuterWrap.offset().top + $contentOuterWrap.outerHeight();
			const fixedTriggerBottom = scrollTop + triggerHeight;

			// Should be sticky if scrolled past initial position and not at bottom
			const shouldBeSticky = scrollTop >= initialTriggerOffset && fixedTriggerBottom < contentBottom;

			if (shouldBeSticky && !isSticky) {
				// Add padding to content to prevent jumping
				$contentOuterWrap.css('padding-top', triggerHeight + 'px');
				$trigger.css({
					'position': 'fixed',
					'top': '0',
					'z-index': '1000'
				});
				isSticky = true;
			} else if (!shouldBeSticky && isSticky) {
				// Remove padding when not sticky
				$contentOuterWrap.css('padding-top', '');
				$trigger.css({
					'position': '',
					'top': '',
					'z-index': ''
				});
				isSticky = false;
			}
		}

		// Update on scroll
		$(window).on('scroll', updateStickyState);

		// Initial check
		updateStickyState();
	}

	/**
	 * Initialize smooth scroll functionality for mobile menu items
	 *
	 * Sets up click handlers for menu links that provide smooth scrolling
	 * to target sections. Uses the same easing and speed as the desktop
	 * navigation (1vh per 0.5s with ease-in-out timing).
	 *
	 * Also closes the menu after clicking a menu item for better UX.
	 */
	function initMobileSmoothScroll() {
		const $navLinks = $mobileMenu.find('.sub-menu a[href^="#"]');

		if (!$navLinks.length) return;

		/**
		 * Calculate duration based on 1vh per 0.5s speed
		 */
		function calculateDuration(distance) {
			const vh = window.innerHeight;
			const distanceInVh = Math.abs(distance) / vh;
			return (distanceInVh / 2) * 1000;
		}

		/**
		 * Ease-in-out timing function
		 */
		function easeInOut(t) {
			return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
		}

		/**
		 * Smooth scroll to target element
		 */
		function smoothScrollTo($target) {
			if (!$target.length) return;

			const targetOffset = $target.offset().top;
			const currentScroll = $(window).scrollTop();
			const distance = targetOffset - currentScroll;

			if (Math.abs(distance) < 5) return;

			const duration = calculateDuration(distance);
			const startTime = performance.now();

			function animateScroll(currentTime) {
				const elapsed = currentTime - startTime;
				const progress = Math.min(elapsed / duration, 1);
				const easedProgress = easeInOut(progress);

				const newScrollTop = currentScroll + (distance * easedProgress);
				$(window).scrollTop(newScrollTop);

				if (progress < 1) {
					requestAnimationFrame(animateScroll);
				}
			}

			requestAnimationFrame(animateScroll);
		}

		// Add click handlers to menu links
		$navLinks.on('click', function(e) {
			e.preventDefault();
			e.stopPropagation(); // Prevent event bubbling to trigger

			const href = $(this).attr('href');
			if (!href || !href.startsWith('#')) return;

			const targetId = href.substring(1);
			const $target = $('#' + targetId);

			if ($target.length) {
				// Close menu and scroll to section
				closeMenu();
				smoothScrollTo($target);
			}
		});
	}

	// Initialize when document is ready
	// Using requestAnimationFrame ensures all CSS is applied before measuring elements
	// This prevents issues with elements not being properly sized when JavaScript runs
	$(document).ready(function() {
		requestAnimationFrame(function() {
			initMobileImpactNav();
		});
	});

})(document, jQuery);
